import {useEffect, useMemo, useState} from 'react';
import {BackButton, ProgressIndicator} from '@/components/atoms';
import {SaveCancelFooter} from '@/components/molecules';
import {
  PerfectStorePreviewHeader,
  PerfectStorePreviewTabs,
  PerfectStoreRequirementsPanels,
  PERFECT_STORE_PREVIEW_TABS
} from '@/containers/PerfectStore/PerfectStorePreview';
import {UpdateStoreVisitsTab} from '@/containers/PerfectStore/UpdateStoreVisits';
import {usePerfectStoreDetail} from '@/hooks/usePerfectStoreDetail';
import {usePerfectStorePreviewActions} from '@/hooks/usePerfectStorePreviewActions';
import {useAppSelector} from '@/store';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {getPerfectStoreStepConfig} from '@/utils/perfectStoreStepConfig';
import {
  formatPerfectStoreLastUpdated,
  isPerfectStoreSaveDraftEnabled
} from '@/helpers/perfectStoreUtils';
import {
  getPerfectStoreCancelModal,
  isPerfectStoreDisplayInvalid,
  isPerfectStoreSetupInvalid
} from '@/pages/PerfectStoreUtils';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useNavigate} from 'react-router-dom';
import '@/containers/PerfectStore/PerfectStorePreview/perfectStorePreview.scss';
import '@/containers/PerfectStore/PerfectStoreReview/perfectStoreReview.scss';

const stepConfig = getPerfectStoreStepConfig();

export const PerfectStorePreview = () => {
  const navigate = useNavigate();
  const {isLoading} = usePerfectStoreDetail();
  const {
    formData,
    status,
    isEditable,
    disableDraft,
    isDuplicateEnabled,
    isDownloadEnabled,
    showFooter,
    showProgressIndicator,
    isCancel,
    handleEditSection,
    handleDuplicate,
    handleDownload,
    handleCancel,
    onConfirmCancel,
    handleDraftAction,
    handlePublishAction,
    getNavigationState,
    // Update Store Visits tab
    targetId,
    isUpdateStoreVisitsTabEnabled,
    isPublishingUpdate,
    handleUpdateStoreVisitsPublish,
    setRefetchVisits
  } = usePerfectStorePreviewActions();

  const countryCode = useAppSelector(selectCountryCode);
  const commonLanguages = useAppSelector((state) => state.common?.languages);
  const updatedAt = useAppSelector((state) => state?.perfectStore?.updatedAt);
  const createdAt = useAppSelector((state) => state?.perfectStore?.createdAt);

  const [activeTabIndex, setActiveTabIndex] = useState(
    PERFECT_STORE_PREVIEW_TABS.REQUIREMENTS
  );

  const languages = useMemo(
    () => COUNTRY_LANGUAGES_CODES[countryCode] || commonLanguages || [],
    [countryCode, commonLanguages]
  );

  const lastUpdatedLabel = useMemo(() => {
    const formatted = formatPerfectStoreLastUpdated(
      updatedAt || formData?.updatedAt || createdAt || formData?.createdAt
    );
    return formatted ? `Last updated: ${formatted}` : '';
  }, [updatedAt, createdAt, formData?.updatedAt, formData?.createdAt]);

  const handleStepClick = (step) => {
    const stepRoutes = {
      1: ROUTES.PERFECT_STORE_ADD,
      2: ROUTES.PERFECT_STORE_DISPLAY
    };
    if (stepRoutes[step]) {
      navigate(stepRoutes[step], {
        state: getNavigationState(step === 2 ? 'review' : undefined)
      });
    }
  };

  const isUpdateStoreVisitsTab =
    activeTabIndex === PERFECT_STORE_PREVIEW_TABS.UPDATE_STORE_VISITS;

  // AC1: if the Update Store Visits tab becomes ineligible (status changed,
  // or stale navigation) while it's the active tab, fall back to Requirements.
  useEffect(() => {
    if (!isUpdateStoreVisitsTabEnabled && isUpdateStoreVisitsTab) {
      setActiveTabIndex(PERFECT_STORE_PREVIEW_TABS.REQUIREMENTS);
    }
  }, [isUpdateStoreVisitsTabEnabled, isUpdateStoreVisitsTab]);

  // Guard against selecting the Update Store Visits tab while it's disabled.
  const handleTabChange = (index) => {
    if (
      index === PERFECT_STORE_PREVIEW_TABS.UPDATE_STORE_VISITS &&
      !isUpdateStoreVisitsTabEnabled
    ) {
      return;
    }
    setActiveTabIndex(index);
  };

  const publishAction = isUpdateStoreVisitsTab
    ? handleUpdateStoreVisitsPublish
    : handlePublishAction;

  const publishDisabled = isUpdateStoreVisitsTab
    ? isPublishingUpdate
    : isPerfectStoreSetupInvalid(formData) ||
      isPerfectStoreDisplayInvalid(formData, languages);

  if (isLoading) {
    return <p data-testid='perfect-store-preview-loading'>Loading...</p>;
  }

  return (
    <div
      className='perfect-store-preview w-full'
      data-testid='perfect-store-preview'>
      <BackButton id='perfect-store-preview-back' />
      <PerfectStorePreviewHeader
        name={formData?.name}
        lastUpdatedLabel={lastUpdatedLabel}
        onDuplicate={handleDuplicate}
        onDownload={handleDownload}
        isDuplicateEnabled={isDuplicateEnabled}
        isDownloadEnabled={isDownloadEnabled}
      />

      {showProgressIndicator && (
        <ProgressIndicator
          currentStep={3}
          completed={false}
          orientation='horizontal'
          steps={stepConfig.steps}
          titles={stepConfig.titles}
          className='mb-6'
          onStepClick={handleStepClick}
        />
      )}

      <br />

      <PerfectStorePreviewTabs
        activeTabIndex={activeTabIndex}
        onTabChange={handleTabChange}
        isUpdateStoreVisitsTabEnabled={isUpdateStoreVisitsTabEnabled}
        requirementsContent={
          <PerfectStoreRequirementsPanels
            formData={formData}
            languages={languages}
            isEditable={isEditable}
            onEdit={handleEditSection}
          />
        }
        updateStoreVisitsContent={
          <UpdateStoreVisitsTab
            targetId={targetId}
            rewardDate={formData?.rewardDate}
            activeEndDate={formData?.activeEndDate}
            awardPointsWhenTargetMet={formData?.awardPointsWhenTargetMet}
            onRefetchVisits={setRefetchVisits}
          />
        }
      />

      {showFooter && (
        <SaveCancelFooter
          isReview
          FormError={publishDisabled}
          handleCancel={handleCancel}
          draftAction={handleDraftAction}
          nextAction={publishAction}
          disableDraft={disableDraft || !isPerfectStoreSaveDraftEnabled(status)}
        />
      )}

      {isCancel &&
        getPerfectStoreCancelModal({
          show: isCancel,
          onConfirm: onConfirmCancel,
          onClose: handleCancel
        })}
    </div>
  );
};

export default PerfectStorePreview;
